import type { Page } from "./types";

export interface CursorRequest {
  cursor: string | undefined;
  signal?: AbortSignal | undefined;
}

/**
 * Adapts a "fetch one page by cursor" function to TanStack Query's infinite queries
 * without importing it:
 *
 *   useInfiniteQuery({
 *     queryKey: ["households"],
 *     ...cursorPaging(({ cursor, signal }) => api.members.listHouseholds({ cursor, signal })),
 *   });
 *   const rows = flattenPages(query.data);
 */
export function cursorPaging<T>(fetchPage: (request: CursorRequest) => Promise<Page<T>>) {
  return {
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }: { pageParam: string | undefined; signal?: AbortSignal }) =>
      fetchPage({ cursor: pageParam, signal }),
    getNextPageParam: (lastPage: Page<T>): string | undefined => lastPage.next_cursor ?? undefined,
  };
}

/** All items of all loaded pages, in order. */
export function flattenPages<T>(data: { pages: Page<T>[] } | undefined): T[] {
  return data ? data.pages.flatMap((page) => page.items) : [];
}

/** Follow cursors until the end (or `maxPages`). For small reference lists such as roles. */
export async function fetchAllPages<T>(
  fetchPage: (request: CursorRequest) => Promise<Page<T>>,
  options: { maxPages?: number; signal?: AbortSignal } = {},
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | undefined;
  for (let pages = 0; pages < (options.maxPages ?? 50); pages += 1) {
    const page = await fetchPage({ cursor, signal: options.signal });
    items.push(...page.items);
    cursor = page.next_cursor ?? undefined;
    if (!cursor) break;
  }
  return items;
}
